import { CustomerRole, InternalRole } from '@edgebox/data-definitions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons/faChevronLeft';
import React, { PropsWithChildren } from 'react';
import { Link } from 'react-router-dom';
import { ApiComponent, IApiComponentState, SyncCoreDataDefinitionsEnumValue, WithMostRecentContractRevision } from '../common';
import { API_DOMAIN } from '../constants';
import { ICONS } from './Icons';
import { ILocationProp, withLocation } from './RouterHelper';
import { IAppContextProp, withAppContext } from '../common/contexts/AppContext';
import { ButtonLink, Alert } from '@edgebox/react-components';
import { Package, Product, SiteApplicationType } from '@edgebox/sync-core-data-definitions';
import { ViewUpgradeButton } from './Shared/ViewUpgradeButton';

interface IProps extends ILocationProp, IAppContextProp, React.JSX.IntrinsicAttributes {}

interface IState extends IApiComponentState {}

interface IMenuItemProps extends PropsWithChildren {
  to?: string;
  className?: string;
  id: string;
  icon: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}
function MenuItem(props: IMenuItemProps) {
  const content = (
    <>
      <div className={`flex-shrink-0 flex-grow-0 p-2 me-2 ${props.active ? 'text-white' : 'text-black'}`}>{props.icon}</div>
      <div className={`flex-shrink-1 flex-grow-1 ${props.active ? 'text-white fw-bold' : 'text-black'}`}>{props.children}</div>
    </>
  );

  const className = `d-flex flex-row align-items-center p-1 rounded text-decoration-none ${props.className || ''} ${
    props.active ? 'bg-primary' : ''
  }`;

  return props.to ? (
    <Link to={props.to} className={className} id={props.id} onClick={props.onClick}>
      {content}
    </Link>
  ) : (
    <div className={className} id={props.id} onClick={props.onClick}>
      {content}
    </div>
  );
}

function matchPath(current: string, link: string) {
  return (
    current === link ||
    current.substring(0, link.length + 1) === link + '/' ||
    (current.substring(0, 10) === '/projects/' &&
      ('/' + current.split('/').slice(3).join('/') === link ||
        ('/' + current.split('/').slice(3).join('/')).substring(0, link.length + 1) === link + '/'))
  );
}

class SidebarNavigationComponent extends ApiComponent<IProps, IState> {
  async load() {
    return {};
  }

  public render() {
    const { location, appContext } = this.props;
    const { customer, contract, project, projectHasAnySites } = appContext;

    const customerRoles = this.api.currentUser?.customerRoles;
    const canViewContent = !!customerRoles?.find((c) =>
      [CustomerRole.Owner, CustomerRole.ManageSites, CustomerRole.ManageContent].includes(c)
    );

    const internalRoles = this.api.currentUser?.customer?.getId() === customer?.id ? this.api.currentUser?.internalRoles : undefined;
    const canAdministerAccounting = internalRoles?.includes(InternalRole.Admin) || internalRoles?.includes(InternalRole.DevOps);
    const canAdministerSyndication = internalRoles?.includes(InternalRole.Admin) || internalRoles?.includes(InternalRole.DevOps);
    const canAdministerOperations = internalRoles?.includes(InternalRole.DevOps);

    const mode = matchPath(location.pathname, '/backend')
      ? 'backend'
      : project?.appType === SiteApplicationType.ContentCloud
        ? 'content-cloud'
        : projectHasAnySites
          ? 'frontend'
          : 'get-started';

    return (
      <div className="sidebar d-flex flex-column bg-white h-100" style={{ width: '250px' }}>
        <div className="pt-3 pb-3" />

        {mode === 'content-cloud' && (
          <div className="flex-shrink-1 flex-grow-1 ps-2 pe-2 overflow-auto">
            <>
              <MenuItem
                id="menu-content-cloud-onboarding"
                icon={
                  <FontAwesomeIcon
                    icon={ICONS.Onboarding[location.pathname === '/' || location.pathname.endsWith('/onboarding') ? 'regular' : 'light']}
                  />
                }
                active={location.pathname === '/' || location.pathname.endsWith('/onboarding')}
                to={'/onboarding'}
              >
                Getting started
              </MenuItem>
            </>

            <div className="border border-1 my-2" />

            <>
              <MenuItem
                id="menu-content-cloud-content-model"
                icon={
                  <FontAwesomeIcon
                    icon={ICONS.ContentTypes[!!matchPath(location.pathname, '/content-cloud/content-model') ? 'regular' : 'light']}
                  />
                }
                active={!!matchPath(location.pathname, '/content-cloud/content-model')}
                to={'/content-cloud/content-model'}
              >
                Content model
              </MenuItem>
            </>

            <>
              <MenuItem
                id="menu-content-cloud-content"
                icon={
                  <FontAwesomeIcon icon={ICONS.Content[!!matchPath(location.pathname, '/content-cloud/content') ? 'regular' : 'light']} />
                }
                active={!!matchPath(location.pathname, '/content-cloud/content')}
                to={'/content-cloud/content'}
              >
                Content
              </MenuItem>
            </>

            <>
              <MenuItem
                id="menu-content-cloud-assets"
                icon={
                  <FontAwesomeIcon icon={ICONS.Assets[!!matchPath(location.pathname, '/content-cloud/assets') ? 'regular' : 'light']} />
                }
                active={!!matchPath(location.pathname, '/content-cloud/assets')}
                to={'/content-cloud/assets'}
              >
                Media
              </MenuItem>
            </>

            <>
              <MenuItem
                id="menu-content-cloud-tags"
                icon={<FontAwesomeIcon icon={ICONS.Tags[!!matchPath(location.pathname, '/content-cloud/tags') ? 'regular' : 'light']} />}
                active={!!matchPath(location.pathname, '/content-cloud/tags')}
                to={'/content-cloud/tags'}
              >
                Tags
              </MenuItem>
            </>

            <>
              <MenuItem
                id="menu-content-cloud-content-user-data"
                icon={
                  <FontAwesomeIcon
                    icon={ICONS.ContentUserData[!!matchPath(location.pathname, '/content-cloud/content-user-data') ? 'regular' : 'light']}
                  />
                }
                active={!!matchPath(location.pathname, '/content-cloud/content-user-data')}
                to={'/content-cloud/content-user-data'}
              >
                User data
              </MenuItem>
            </>

            <div className="border border-1 my-2" />

            <>
              <MenuItem
                id="menu-content-cloud-rest-explorer"
                icon={
                  <FontAwesomeIcon
                    icon={ICONS.RestExplorer[!!matchPath(location.pathname, '/content-cloud/rest-explorer') ? 'regular' : 'light']}
                  />
                }
                active={!!matchPath(location.pathname, '/content-cloud/rest-explorer')}
                to={'/content-cloud/rest-explorer'}
              >
                REST Explorer
              </MenuItem>
            </>

            <>
              <MenuItem
                id="menu-content-cloud-graphql-explorer"
                icon={
                  <FontAwesomeIcon
                    icon={ICONS.GraphQlExplorer[!!matchPath(location.pathname, '/content-cloud/graphql-explorer') ? 'regular' : 'light']}
                  />
                }
                active={!!matchPath(location.pathname, '/content-cloud/graphql-explorer')}
                to={'/content-cloud/graphql-explorer'}
              >
                GraphQL Explorer
              </MenuItem>
            </>

            <>
              <MenuItem
                id="menu-content-cloud-client-generation"
                icon={
                  <FontAwesomeIcon
                    icon={
                      ICONS.IntegrationClients[!!matchPath(location.pathname, '/content-cloud/client-generation') ? 'regular' : 'light']
                    }
                  />
                }
                active={!!matchPath(location.pathname, '/content-cloud/client-generation')}
                to={'/content-cloud/client-generation'}
              >
                Client generation
              </MenuItem>
            </>

            {/*<>
              <MenuItem
                id="menu-content-cloud-integration"
                icon={
                  <FontAwesomeIcon
                    icon={ICONS.IntegrationClients[!!matchPath(location.pathname, '/content-cloud/integration') ? 'regular' : 'light']}
                  />
                }
                active={!!matchPath(location.pathname, '/content-cloud/integration')}
                to={'/content-cloud/integration'}
              >
                Integration
              </MenuItem>
            </>*/}
          </div>
        )}

        {(mode === 'frontend' || mode === 'get-started') && (
          <div className="flex-shrink-1 flex-grow-1 ps-2 pe-2 overflow-auto">
            <>
              {mode === 'frontend' && appContext.project && (
                <MenuItem
                  id="menu-syndication-dashboard"
                  icon={<FontAwesomeIcon icon={ICONS['Dashboard'][!!matchPath(location.pathname, '/dashboard') ? 'regular' : 'light']} />}
                  active={!!matchPath(location.pathname, '/dashboard')}
                  to={`projects/${project?.id}/dashboard`}
                >
                  Dashboard
                </MenuItem>
              )}

              <MenuItem
                id="menu-syndication-getting-started"
                icon={<FontAwesomeIcon icon={ICONS.Onboarding[!!matchPath(location.pathname, '/onboarding') ? 'regular' : 'light']} />}
                active={location.pathname.endsWith('/onboarding')}
                to={'/onboarding'}
              >
                Getting started
              </MenuItem>

              {mode === 'frontend' && (
                <>
                  <div className="border border-1 my-2" />

                  {canViewContent && (
                    <MenuItem
                      id="menu-syndication-content"
                      icon={<FontAwesomeIcon icon={ICONS.Content[matchPath(location.pathname, '/content') ? 'regular' : 'light']} />}
                      active={!!matchPath(location.pathname, '/content')}
                      to={'/content'}
                    >
                      Content
                    </MenuItem>
                  )}

                  {canViewContent && (
                    <MenuItem
                      id="menu-syndication-content"
                      icon={<FontAwesomeIcon icon={ICONS.Updates[matchPath(location.pathname, '/updates') ? 'regular' : 'light']} />}
                      active={!!matchPath(location.pathname, '/updates')}
                      to={'/updates'}
                    >
                      Updates
                    </MenuItem>
                  )}
                </>
              )}
            </>

            {contract && (
              <WithMostRecentContractRevision contractId={contract.id}>
                {(revision) => {
                  let warning: string = '';
                  if (!revision.current) {
                    warning = 'Your contract has expired.';
                  } else if (revision.current.packageType === Package.Trial && !revision.next) {
                    warning = 'Your trial expires soon.';
                  }

                  if (warning) {
                    return (
                      <>
                        <div className="" style={{ borderBottom: '2px solid #eee' }} />

                        <Alert variant="danger" className="mt-3">
                          <p className="mb-0">{warning}</p>

                          <ViewUpgradeButton contractId={contract.id} />
                        </Alert>
                      </>
                    );
                  }
                }}
              </WithMostRecentContractRevision>
            )}
          </div>
        )}

        {mode === 'backend' && (
          <div className="flex-shrink-1 flex-grow-1 ps-2 pe-2 overflow-auto">
            {canAdministerAccounting && (
              <>
                <div className={'ps-2 mt-2 headline text-uppercase text-danger'}>Accounting</div>
                <MenuItem
                  id="menu-accounting-admin-customers"
                  icon={
                    <FontAwesomeIcon
                      icon={ICONS.Customer[matchPath(location.pathname, '/backend/billing/customers') ? 'regular' : 'light']}
                    />
                  }
                  active={!!matchPath(location.pathname, '/backend/billing/customers')}
                  to={'/backend/billing/customers'}
                >
                  Customers
                </MenuItem>
                <MenuItem
                  id="menu-accounting-admin-invoices"
                  icon={
                    <FontAwesomeIcon
                      icon={ICONS.Invoice[matchPath(location.pathname, '/backend/billing/invoices') ? 'regular' : 'light']}
                    />
                  }
                  active={!!matchPath(location.pathname, '/backend/billing/invoices')}
                  to={'/backend/billing/invoices'}
                >
                  Invoices
                </MenuItem>
                <MenuItem
                  id="menu-accounting-admin-contracts"
                  icon={
                    <FontAwesomeIcon
                      icon={ICONS.Contract[matchPath(location.pathname, '/backend/billing/contracts') ? 'regular' : 'light']}
                    />
                  }
                  active={!!matchPath(location.pathname, '/backend/billing/contracts')}
                  to={'/backend/billing/contracts'}
                >
                  Contracts
                </MenuItem>
              </>
            )}

            {canAdministerSyndication && (
              <>
                <div className={'ps-2 mt-2 headline text-uppercase text-danger'}>
                  <SyncCoreDataDefinitionsEnumValue enumName={'Product'} keyName={Product.Syndication} />
                </div>
                <MenuItem
                  id="menu-syndication-admin-sites"
                  icon={
                    <FontAwesomeIcon icon={ICONS.Site[matchPath(location.pathname, '/backend/syndication/sites') ? 'regular' : 'light']} />
                  }
                  active={!!matchPath(location.pathname, '/backend/syndication/sites')}
                  to={'/backend/syndication/sites'}
                >
                  Sites
                </MenuItem>
                <MenuItem
                  id="menu-syndication-admin-sync-cores"
                  icon={
                    <FontAwesomeIcon
                      icon={ICONS.SyncCore[matchPath(location.pathname, '/backend/syndication/sync-cores') ? 'regular' : 'light']}
                    />
                  }
                  active={!!matchPath(location.pathname, '/backend/syndication/sync-cores')}
                  to={'/backend/syndication/sync-cores'}
                >
                  Sync Cores
                </MenuItem>

                <MenuItem
                  id="menu-syndication-admin-formula"
                  icon={
                    <FontAwesomeIcon
                      icon={ICONS.Setting[matchPath(location.pathname, '/backend/syndication/formula') ? 'regular' : 'light']}
                    />
                  }
                  active={!!matchPath(location.pathname, '/backend/syndication/formula')}
                  to={'/backend/syndication/formula'}
                >
                  Formula
                </MenuItem>

                <MenuItem
                  id="menu-syndication-admin-ai"
                  icon={
                    <FontAwesomeIcon icon={ICONS.Setting[matchPath(location.pathname, '/backend/syndication/ai') ? 'regular' : 'light']} />
                  }
                  active={!!matchPath(location.pathname, '/backend/syndication/ai')}
                  to={'/backend/syndication/ai/provider'}
                >
                  AI
                </MenuItem>
              </>
            )}

            {canAdministerOperations && (
              <>
                <div className={'ps-2 mt-2 headline text-uppercase text-danger'}>Operations</div>
                <MenuItem
                  id="menu-settings"
                  icon={<FontAwesomeIcon icon={ICONS.Setting[matchPath(location.pathname, '/backend/settings') ? 'regular' : 'light']} />}
                  active={!!matchPath(location.pathname, '/backend/settings')}
                  to={'/backend/settings'}
                >
                  Settings
                </MenuItem>
                {API_DOMAIN.includes('localhost') && (
                  <MenuItem
                    id="menu-developer"
                    icon={
                      <FontAwesomeIcon icon={ICONS.Setting[matchPath(location.pathname, '/backend/developer') ? 'regular' : 'light']} />
                    }
                    active={!!matchPath(location.pathname, '/backend/developer')}
                    to={'/backend/developer'}
                  >
                    Developer
                  </MenuItem>
                )}
                <MenuItem
                  id="menu-syndication-admin-logs"
                  icon={
                    <FontAwesomeIcon icon={ICONS.Logs[matchPath(location.pathname, '/backend/syndication/logs') ? 'regular' : 'light']} />
                  }
                  active={!!matchPath(location.pathname, '/backend/syndication/logs')}
                  to={'/backend/syndication/logs'}
                >
                  Logs
                </MenuItem>
              </>
            )}
          </div>
        )}
      </div>
    );
  }
}

export const SidebarNavigation = withLocation<Omit<IProps, 'appContext' | 'location'>>(withAppContext(SidebarNavigationComponent));
